import { useState } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { createTheme, Box, Stack, Typography, IconButton, Grid, Modal, Popover } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { getSubscribedMails, mails, updateSubscribedMails } from "./helper";
import TableCellsRow from "./TableCell";
import Fields from "./Fields";
import NoResultFoundFilters from "../../../../atoms/noResultsFilters";
import SkeletonLoader from "../../../../atoms/project-details/SkeletonLoader";
import TableViewCommon from "../../../../molecules/table-view-common";
import CustomBtnFilled from "../../../../atoms/form-molecules/CustomBtnFilled";
import FormCancelSubmitBtns from "../../../../atoms/formCancelSubmitBtns";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { isSuperAdmin } from "../../../../utils/role";
import LinkIcon from '@mui/icons-material/Link';


interface ViewAgendaProps {
  onClose: () => void;
  projectId: string | null;
}

const emailTableColumns = [
  {
    id: "id",
    label: "Email Id",
    numeric: false,
    disablePadding: false,
    isSort: false,
  },
  {
    id: "actions",
    label: "",
    numeric: false,
    disablePadding: false,
    isSort: false,
  },
];

const AddMail: React.FC<ViewAgendaProps> = ({ onClose, projectId }) => {
  const navigate = useNavigate();
  const [mailState, setMailState] = useState<mails>({
    fetched_mails: [],
    loading: false,
    changed: false,
    deleted: [],
    added: [],
    projectName: '',
    workstreams: [],
  });
  const [popup, SetPopup] = useState(false);
  const [workstreamPopup, SetWorkstreamPopup] = useState(false);
  const [editMail, setEditMail] = useState(false);
  const defaultValues = { enter_email: null };
  const methods = useForm({ defaultValues });
  const { setError } = methods;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);


  const onSubmit: SubmitHandler<{ enter_email: string | null }> = (formData) => {
  const found = mailState?.fetched_mails?.includes(formData?.enter_email);

    if (!found) {
      setMailState((prev) => ({
        ...prev,
        fetched_mails: [formData?.enter_email, ...prev?.fetched_mails],
        changed: true,
        deleted: (prev?.deleted??[])?.filter((mail) => mail !== formData?.enter_email),
        added: [...prev?.added, formData?.enter_email],
      }));
    }
    else {
      setError("enter_email", {
        type: "manual",
        message: "This email already exists in the list",
      });
      return;
    }

    methods.reset();

  };

  const defaultTheme = createTheme({
    typography: {
      fontFamily: ["Montserrat"].join(","),
      fontSize: 12,
    },
    palette: {
      primary: {
        main: "#EC9324",
      },
    },
  });

  const handleDelete = (row: string) => {
    setMailState((prev) => ({
      ...prev,
      fetched_mails: (prev?.fetched_mails ?? [])?.filter((mail) => mail !== row),
      changed: true,
      added: (prev?.added ?? [])?.filter((mail) => mail !== row),
      deleted: [...prev?.deleted, row],
    }));
  };

  const handleUpdate = () => SetPopup(true);

  const handleRevert = () => getSubscribedMails(setMailState, projectId);

  const handleSubmit = () => {
    updateSubscribedMails(setMailState, mailState, projectId);
    SetPopup(false);
  };

  const getMails = () => {
    setEditMail(true);
    getSubscribedMails(setMailState, projectId);
  };

  return (
    <>
      {editMail ? (
        popup ? (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              width: 700,
              minHeight: 400,
              borderRadius: '16px',
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Stack sx={{ mt: "20px", maxHeight: "300px", overflowY: "auto" }} padding={"10px 0px"} spacing={3}>
              <Box sx={{ fontSize: "large" }}>Please Confirm Your Changes</Box>

              {mailState?.deleted.length > 0 && (
                <Stack spacing={"2px"}>
                  <Box>Removed Mails</Box>
                  <Stack spacing={"2px"}>
                    {mailState?.deleted?.map((el, key) => (
                      <Box
                        key={key}
                        padding={"5px 10px"}
                        sx={{
                          fontSize: "small",
                          width: "600px",
                          borderLeft: 4,
                          borderColor: "#FF285F",
                          bgcolor: "#F7F7F9",
                        }}
                      >
                        {el}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              )}

              {mailState?.added.length > 0 && (
                <Stack spacing={"2px"}>
                  <Box>Added Mails</Box>
                  <Stack spacing={"2px"}>
                    {mailState?.added?.map((el, key) => (
                      <Box
                        key={key}
                        padding={"5px 10px"}
                        sx={{
                          fontSize: "small",
                          width: "600px",
                          borderLeft: 4,
                          borderColor: "#4FB29C",
                          bgcolor: "#F7F7F9",
                        }}
                      >
                        {el}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>

            <Stack>
              <FormCancelSubmitBtns handleSubmitBtnClick={handleSubmit} handleClose={() => SetPopup(false)} />
            </Stack>
          </Box>
        ) : (
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column", width: "100%", height: "100%", pt: 2 }}>
            {mailState.loading && (
              <SkeletonLoader
                width="100%"
                height="300px"
                style={{ position: "absolute", top: 0, right: 0, left: 0, bottom: 0, zIndex: 10 }}
              />
            )}

            <Stack>
              <ThemeProvider theme={defaultTheme}>
                <Grid container padding={"5px 10px"}>
                  <Grid item xs={11}>
                    <Typography
                      sx={{
                        width: "100%",
                        fontSize: "20px",
                        fontWeight: 500,
                        fontFamily: "Montserrat",
                        color: 'primary.main'
                      }}
                    >
                      {`Project Name: ${mailState?.projectName}`}
                    </Typography>
                  </Grid>

                  <Grid item xs={1}>
                    <IconButton 
                    onClick={(e) =>{
                      SetWorkstreamPopup(!workstreamPopup)
                      setAnchorEl(e.currentTarget);
                    }}
                    >
                      <LinkIcon
                        aria-label="Linked Workstreams"
                        sx={{ display: 'block', height: '23px', width: '23px', color: 'grey.500' }}
                      />
                    </IconButton>
                  </Grid>

                  <Popover
                    open={Boolean(anchorEl) && workstreamPopup}
                    onClose={() => SetWorkstreamPopup(false)}
                    sx={{
                      ml: -25
                    }}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}

                  >
                    <Box
                      padding={"0px 20px"}
                      sx={{
                        overflowY: 'auto',
                        width: "250px",
                        borderRadius: "5%",
                        bgcolor: "#FFFFFF",
                        color: "#EC9324",
                        px: "5px",
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: "5px",
                        maxHeight: "150px",
                      }}
                    >
                      <Stack
                        padding={"10px 10px"}
                        direction="row"
                        sx={{ width: '100%' }}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 300,
                            fontFamily: "Montserrat",
                            color: "black"
                          }}
                          textAlign="center"
                        >
                          Linked Workstreams
                        </Typography>
                        <CloseIcon sx={{ cursor: "pointer" }} onClick={() => SetWorkstreamPopup(false)} />
                      </Stack>

                      <Grid padding={"10px 0px"} container justifyContent="center" spacing={2}>
                        {mailState?.workstreams?.map((stream, key) => (
                          <Grid
                            item
                            xs={5}
                            key={key}
                            sx={{ width: "100%", fontSize: "15px", fontWeight: 300, fontFamily: "Montserrat" }}
                            textAlign="center"
                          >
                            <Typography
                              sx={{ textDecoration: 'underline', cursor: "pointer" }}
                              onClick={() => {
                                navigate(`/layout/projects/projectdetails?id=${stream}&page=1`,{replace: true});
                                onClose();
                              }}
                            >
                              {stream}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Popover>
                </Grid>

                {isSuperAdmin() && (
                  <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                      <Fields />
                    </form>
                  </FormProvider>
                )}
              </ThemeProvider>
            </Stack>

            <Stack padding={"0px 20px"}>
              {!mailState.loading && (!mailState?.fetched_mails?.length ? (
                <NoResultFoundFilters text="Empty" />
              ) : (
                <TableViewCommon
                  rows={mailState?.fetched_mails}
                  totalSelected={0}
                  title=""
                  headCells={emailTableColumns}
                  isSelected={false}
                  selectAllowed={() => false}
                  handleTableRowClick={() => {}}
                  tableCells={(row) => <TableCellsRow row={row} handleDelete={handleDelete} />}
                  isItemSelected={() => false}
                  handleSelectAllClick={() => {}}
                  containerSx={{
                    maxHeight: 300,
                    overflowX: "clip",
                    overflowY: "auto",
                    display: "flex",
                    alignItems: "start",
                  }}
                  hidePagination
                />
              ))}
            </Stack>

            {isSuperAdmin() && (
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }} padding={"10px 160px"}>
                <CustomBtnFilled
                  styles={{ height: "35px", fontSize: "15px", fontWeight: 300, fontFamily: "Montserrat" }}
                  label="Update Changes"
                  variant="contained"
                  disabled={!mailState?.changed}
                  onClick={handleUpdate}
                />
                <CustomBtnFilled
                  variant="outlined"
                  styles={{ height: "35px", fontSize: "15px", fontWeight: 300, fontFamily: "Montserrat" }}
                  label="Revert Changes"
                  disabled={!mailState?.changed}
                  onClick={handleRevert}
                />
              </Box>
            )}
          </Box>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            minHeight: 400,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CustomBtnFilled
            styles={{ fontSize: '15px', fontWeight: 300, fontFamily: 'Montserrat' }}
            label="get mails"
            variant="contained"
            onClick={getMails}
          />
        </Box>
      )}
    </>
  );
};

export default AddMail;
